import { getUserId } from './helperMethods';
import {
  ConsentOptOutSource,
  customerBusinessService,
  IConsentTrackingEventPayload,
} from '../services/customer.service';

type CookieYesConsentDetail = {
  accepted?: string[];
  rejected?: string[];
};

const COOKIE_CATEGORIES = [
  'necessary',
  'functional',
  'analytics',
  'performance',
  'advertisement',
] as const;

type CookieCategory = (typeof COOKIE_CATEGORIES)[number];

const hasGlobalPrivacyControl = (): boolean => {
  try {
    return (
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  } catch (_) {
    return false;
  }
};

/**
 * Attributes the opt-out to whichever signal caused it. GPC wins over the
 * banner: it is a browser-level legal signal that stands regardless of what the
 * visitor later clicks. 'default' means no opt-out is in effect.
 */
const resolveOptOutSource = (
  hasAdvertisementConsent: boolean
): ConsentOptOutSource => {
  if (hasGlobalPrivacyControl()) return 'gpc';
  if (!hasAdvertisementConsent) return 'banner_click';
  return 'default';
};

const resolveCustomerId = (): number | null => {
  const userId = getUserId();
  if (!userId) return null;
  const parsed = Number(userId);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildConsentTrackingPayload = (
  detail: CookieYesConsentDetail
): IConsentTrackingEventPayload => {
  const accepted = new Set(detail.accepted ?? []);
  const categories = COOKIE_CATEGORIES.reduce(
    (acc, category: CookieCategory) => {
      acc[category] = accepted.has(category);
      return acc;
    },
    {} as Record<CookieCategory, boolean>
  );

  const optOutSource = resolveOptOutSource(categories.advertisement);
  const shouldOptOut = optOutSource !== 'default';

  return {
    ...categories,
    shouldOptOut,
    opt_out_source: optOutSource,
    customer_id: resolveCustomerId(),
  };
};

export const persistCookieYesConsent = async (
  detail: CookieYesConsentDetail
): Promise<void> => {
  const payload = buildConsentTrackingPayload(detail);
  await customerBusinessService.trackConsentEvent(payload);
};

/**
 * Listens for CookieYes banner preference changes (accept / reject / custom
 * save, including footer "Do Not Sell..." revisit) and posts consent to the BE.
 * Returns a cleanup function to remove the listener.
 */
export const initCookieYesConsentTracking = (): (() => void) => {
  const onConsentUpdate = (event: Event) => {
    const detail = (event as CustomEvent<CookieYesConsentDetail>).detail;
    if (!detail) return;

    void persistCookieYesConsent(detail).catch((error) => {
      console.error('Failed to persist CookieYes consent event', error);
    });
  };

  document.addEventListener('cookieyes_consent_update', onConsentUpdate);
  return () => {
    document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
  };
};
